import { createContext } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { useUUID } from '@medifind/router';
import { Bookmark, MFLink, Skeleton } from '@medifind/shared-basic-components';
import { getSmallClassList } from '@medifind/utils';
import CardActionButtonWrapper from './CardActionButtonWrapper';
import styles from './Card.module.scss';

export const CardContext = createContext({});

const Title = ({
  priority,
  title,
  link,
  onClick,
  titleSkeletonLines,
  hasData,
  classes,
  smallClassList,
  smallClassListReverse,
  isBodyTitle,
  id,
  rel,
}) => {
  const HeaderTag = priority ? `h${priority}` : 'div';
  if (!title) return null;
  const content = (
    <HeaderTag
      className={classNames(
        styles['body__title'],
        classes.title,
        smallClassList,
        smallClassListReverse,
        styles[`is-${isBodyTitle ? 'body' : 'header'}-title`],
      )}
      id={id}
    >
      {hasData ? (
        typeof title === 'string' ? (
          <span dangerouslySetInnerHTML={{ __html: title }} />
        ) : (
          title
        )
      ) : (
        <Skeleton count={titleSkeletonLines} />
      )}
    </HeaderTag>
  );

  if (link && hasData && link.type !== 'icon') {
    return (
      <MFLink
        {...(link ?? {})}
        onClick={onClick}
        className={classNames(
          styles['title-link'],
          styles[`is-${isBodyTitle ? 'body' : 'header'}-title`],
          smallClassList,
          smallClassListReverse,
        )}
        rel={rel}
      >
        {content}
      </MFLink>
    );
  }
  return content;
};
const Icon = ({ icon, link, rel, onClick, classes, hasData, ariaLabelledBy }) => {
  const content = <div className={classNames(styles['body__icon'], classes.icon)}>{icon}</div>;
  if (link && hasData && link.type !== 'title') {
    return (
      <MFLink tabIndex="-1" rel={rel} {...(link ?? {})} aria-labelledby={link['aria-label'] ? null : ariaLabelledBy} onClick={onClick}>
        {content}
      </MFLink>
    );
  }
  return content;
};

const Card = ({
  id,
  style,
  children,
  classes = {},
  header,
  icon,
  hideIcon,
  priority,
  title,
  titleSkeletonLines = 3,
  hasData,
  metadata = {},
  buttons,
  smallAt = ['xs'],
  bookmark,
  noSpaceBetween,
  footer,
  cardTop,
  sideContainer,
  link,
  onClick,
  rel,
}) => {
  const { smallClassList, smallClassListReverse } = getSmallClassList(smallAt, styles);
  const titleId = useUUID();

  return (
    <CardContext.Provider value={{ smallAt }}>
      <div
        className={classNames(
          styles['card'],
          {
            [styles['no-space-between']]: noSpaceBetween,
          },
          classes.card,
        )}
        id={id}
        style={{ ...(style ?? {}) }}
      >
        {cardTop && <div className={classNames(styles['card-top'], classes.cardTop)}>{cardTop}</div>}
        {header}
        {/* body/data */}
        <div className={classNames(styles['body__wrapper'], { [styles['has-bookmark']]: !!bookmark }, smallClassList, classes.card)}>
          <div className={classNames(styles['body__title-wrapper'], smallClassList, classes.cardTitleWrapper)}>
            {bookmark && (
              <Bookmark classes={{ container: styles['bookmark'] }} color="transparent-blue" buttonType="box" data={bookmark} iconOnly />
            )}
            {(!hideIcon || sideContainer) && (
              <div className={classNames(styles['side-container'], styles['side-container--side'], smallClassList)}>
                {!hideIcon && <Icon {...{ icon, link, rel, onClick, classes, hasData, ariaLabelledBy: titleId }} />}

                {sideContainer && <div className={classNames(styles['side-container--side-content'], smallClassList)}>{sideContainer}</div>}
              </div>
            )}
            <Title
              {...{ priority, title, link, rel, onClick, titleSkeletonLines, hasData, classes, smallClassList, smallClassListReverse }}
              isBodyTitle={false}
              id={titleId}
            />
          </div>

          {sideContainer && (
            <div className={classNames(styles['side-container'], styles['side-container--span'], smallClassListReverse)}>
              {sideContainer}
            </div>
          )}
          <div className={classNames(styles['body__content-wrapper'], smallClassList, classes.metadataButtonsWrapper)}>
            <Title
              {...{ priority, title, link, rel, onClick, titleSkeletonLines, hasData, classes, smallClassList, smallClassListReverse }}
              isBodyTitle={true}
              id={titleId}
            />

            {metadata.first}
            {/* metadata */}
            {metadata.keyValue
              ?.filter((m) => m && (!hasData || m.value))
              ?.map(({ label, value, skeletonWidth, skeletonCount }, i, arr) => {
                return hasData ? (
                  <div
                    key={`${label}-${i}`}
                    className={classNames(styles['body__metadata'], arr.length - 1 === i && styles['last'], classes.metadata)}
                  >
                    <b className={classNames(classes.label)}>{label} </b>
                    {value}
                  </div>
                ) : (
                  <Skeleton key={`skel-${label}-${i}`} width={skeletonWidth ?? 200} count={skeletonCount} />
                );
              })}
            {metadata.last}
          </div>
          {/* {metadata.flexTail}
        </div>
        {children} */}
          {children}
        </div>
        {buttons && (
          <CardActionButtonWrapper classes={classes} useOffset={false}>
            {buttons}
          </CardActionButtonWrapper>
        )}
        {footer && <div className={classNames(styles['footer'], classes.footer)}>{footer}</div>}
      </div>
    </CardContext.Provider>
  );
};

Card.propTypes = {
  id: PropTypes.string,
  style: PropTypes.object,
  children: PropTypes.any,
  classes: PropTypes.object,
  header: PropTypes.node,
  icon: PropTypes.node,
  hideIcon: PropTypes.bool,
  hasData: PropTypes.bool,
  noSpaceBetween: PropTypes.bool,
  bookmark: PropTypes.object,
  sideContainer: PropTypes.any,
  link: PropTypes.shape({
    to: PropTypes.string,
    target: PropTypes.string,
    tabIndex: PropTypes.string,
    disabled: PropTypes.bool,
    type: PropTypes.oneOf(['title', 'icon', 'both']),
    ariaLabel: PropTypes.string,
  }),
  rel: PropTypes.string,
  onClick: PropTypes.func,
  metadata: PropTypes.shape({
    first: PropTypes.any,
    last: PropTypes.any,
    flexTail: PropTypes.any,
    footer: PropTypes.any,
    keyValue: PropTypes.arrayOf(
      PropTypes.oneOfType([
        PropTypes.shape({
          label: PropTypes.any.isRequired,
          value: PropTypes.any,
          skeletonWidth: PropTypes.number,
          skeletonCount: PropTypes.number,
        }),
        PropTypes.bool,
      ]),
    ),
  }),
  buttons: PropTypes.node,
  priority: PropTypes.number,
  title: PropTypes.any,
  footer: PropTypes.any,
  cardTop: PropTypes.any,
  titleSkeletonLines: PropTypes.number,
  smallAt: PropTypes.arrayOf(PropTypes.oneOf(['xs', 'sm', 'md', 'lg', 'xl', 'xxl'])),
};

export { Card as default, Card };
